import { useEffect, useRef } from "react";
import { basicSetup, EditorView } from "codemirror";
import { EditorState } from "@codemirror/state";
import { python } from "@codemirror/lang-python";
import { pythonEditorExtensions } from "../../student-ide/pythonEditorConfig";

const authoringEditorTheme = EditorView.theme(
  {
    "&": {
      backgroundColor: "var(--ide-editor-bg, #fbfaf6)",
      color: "var(--ide-editor-fg, #1f2a22)",
      borderRadius: "0.5rem",
      border: "1px solid rgba(27, 77, 46, 0.18)",
    },
    "&.cm-focused": {
      outline: "2px solid var(--color-forest, #1b4d2e)",
      outlineOffset: "0",
    },
    ".cm-scroller": {
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
      fontSize: "0.85rem",
      lineHeight: 1.5,
    },
    ".cm-content": { caretColor: "var(--color-forest, #1b4d2e)" },
    ".cm-gutters": {
      backgroundColor: "rgba(27, 77, 46, 0.06)",
      color: "var(--color-brown, #6b4f2e)",
      borderRight: "1px solid rgba(27, 77, 46, 0.12)",
    },
    ".cm-activeLineGutter": { backgroundColor: "rgba(107, 163, 54, 0.12)" },
  },
  { dark: false }
);

export interface LessonCodeEditorProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly minHeight?: string;
  readonly ariaLabel?: string;
  readonly disabled?: boolean;
}

/** Controlled CodeMirror Python editor for lesson authoring (starter code, helper files, checks). */
export function LessonCodeEditor({
  value,
  onChange,
  minHeight = "12rem",
  ariaLabel,
  disabled = false,
}: LessonCodeEditorProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const state = EditorState.create({
      doc: value,
      extensions: [
        basicSetup,
        python(),
        ...pythonEditorExtensions,
        authoringEditorTheme,
        EditorView.lineWrapping,
        EditorState.readOnly.of(disabled),
        EditorView.editable.of(!disabled),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onChangeRef.current(update.state.doc.toString());
          }
        }),
      ],
    });
    const view = new EditorView({ state, parent: host });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Recreate when disabled changes; value sync handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  // Keep the editor in sync when the value prop changes externally (e.g. AI regenerate).
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === value) return;
    view.dispatch({
      changes: { from: 0, to: current.length, insert: value },
    });
  }, [value]);

  return (
    <div
      ref={hostRef}
      className="lesson-code-editor"
      role="textbox"
      aria-label={ariaLabel}
      aria-multiline="true"
      style={{ minHeight }}
    />
  );
}
